import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  FaUsers,
  FaEye,
  FaCheck,
  FaTimes,
  FaFileAlt,
  FaSearch,
  FaCalendarAlt,
  FaEnvelope,
  FaPhone,
  FaBriefcase,
  FaClock,
  FaChevronLeft,
  FaChevronRight,
  FaUserCheck,
  FaUserClock,
  FaUserGraduate,
  FaFilePdf,
  FaFileImage,
  FaFileWord,
  FaPrint,
  FaExpand,
  FaCompress,
  FaSearchPlus,
  FaSearchMinus,
  FaSpinner,
} from 'react-icons/fa';
import './artisanapplication.scss';
import { toast } from 'react-toastify';
import { serverUrlAuth } from '../../../serverUrl';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import withAuth from '../../auth/WithAuth';
import FurnitureNavbar from '../../../components/layout/Nav';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const AdminArtisanApplications = () => {
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form data for approve modal
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    tier: 'bronze',
  });

  const [formErrors, setFormErrors] = useState({});

  // PDF states
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [scale, setScale] = useState(1.0);

  // Fetch all applications
  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await serverUrlAuth.get(
        'artisan-applications/admin/applications/',
      );
      console.log('Applications:', response.data);
      setApplications(response.data);
      setFilteredApplications(response.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  // Filter applications based on search and status
  useEffect(() => {
    let filtered = applications;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(
        (app) => app.application_status === statusFilter,
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (app) =>
          app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.phone_number.includes(searchTerm),
      );
    }

    setFilteredApplications(filtered);
    setCurrentPage(1);
  }, [searchTerm, statusFilter, applications]);

  // Update application status only (without creating artisan)
  const updateApplicationStatusOnly = async (applicationId, newStatus) => {
    try {
      const response = await serverUrlAuth.patch(
        `artisan-applications/admin/applications/${applicationId}/update-status/`,
        { application_status: newStatus },
      );

      if (response.status === 200) {
        setApplications((prev) =>
          prev.map((app) =>
            app.id === applicationId
              ? { ...app, application_status: newStatus }
              : app,
          ),
        );
        toast.success(`Application ${newStatus} successfully!`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update application status');
      return false;
    }
  };

  // Create artisan from application data
  const createArtisanFromApplication = async (application, tier) => {
    try {
      const payload = {
        full_name: application.name,
        email: application.email,
        phone_number: application.phone_number,
        tier: tier,
      };

      const response = await serverUrlAuth.post(
        'users/admin/create-artisan/',
        payload,
      );
      return response.data;
    } catch (error) {
      console.error('Error creating artisan:', error);
      if (error.response?.status === 409) {
        toast.error('An artisan with this email already exists.');
      } else if (error.response?.data) {
        const errorMessages = Object.values(error.response.data)
          .flat()
          .join(', ');
        toast.error(`Error creating artisan: ${errorMessages}`);
      } else {
        toast.error('Failed to create artisan account');
      }
      throw error;
    }
  };

  // Approve application with tier selection
  const handleApproveWithTier = async (application) => {
    // Pre-fill form with application data
    setFormData({
      full_name: application.name,
      email: application.email,
      phone_number: application.phone_number,
      tier: 'bronze',
    });
    setSelectedApplication(application);
    setShowApproveModal(true);
  };

  // Submit approval with tier
  const handleSubmitApproval = async (e) => {
    e.preventDefault();

    // Validate form
    const errors = {};
    if (!formData.full_name.trim()) {
      errors.full_name = 'Full name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    }
    if (!formData.phone_number) {
      errors.phone_number = 'Phone number is required';
    }
    if (!formData.tier) {
      errors.tier = 'Tier selection is required';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      // First, create the artisan account
      await createArtisanFromApplication(selectedApplication, formData.tier);

      // Then, update the application status to approved
      await updateApplicationStatusOnly(selectedApplication.id, 'approved');

      toast.success(
        `Application approved! Artisan account created with ${formData.tier.toUpperCase()} tier.`,
      );

      // Close modals and refresh
      setShowApproveModal(false);
      setShowDetailsModal(false);
      setSelectedApplication(null);
      fetchApplications();
    } catch (error) {
      console.error('Error in approval process:', error);
      // If artisan creation fails, don't update the application status
      toast.error('Failed to approve application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reject application
  const handleRejectApplication = async (applicationId) => {
    if (window.confirm('Are you sure you want to reject this application?')) {
      await updateApplicationStatusOnly(applicationId, 'rejected');
    }
  };

  // View application details
  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
    setShowDetailsModal(true);
  };

  // Get tier benefits
  const getTierBenefits = (tier) => {
    switch (tier) {
      case 'bronze':
        return [
          'Basic profile listing',
          'Up to 10 product listings',
          '5% commission on sales',
          'Email support',
          'Basic analytics dashboard',
        ];
      case 'silver':
        return [
          'Enhanced profile with portfolio',
          'Up to 50 product listings',
          '3% commission on sales',
          'Priority email support',
          'Advanced analytics',
          'Featured in category pages',
          'Social media promotion',
        ];
      case 'gold':
        return [
          'Premium profile with video',
          'Unlimited product listings',
          '1% commission on sales',
          '24/7 phone & email support',
          'Premium analytics & insights',
          'Homepage featured placement',
          'Dedicated account manager',
          'Early access to new features',
          'Invitation to artisan events',
        ];
      default:
        return [];
    }
  };

  // Normalize phone number
  const normalizePhoneNumber = (phone) => {
    let cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('0')) {
      cleaned = '254' + cleaned.substring(1);
    } else if (cleaned.startsWith('254')) {
      cleaned = cleaned;
    } else if (cleaned.startsWith('+')) {
      cleaned = cleaned.substring(1);
    }
    return '+' + cleaned;
  };

  // Open document in modal viewer
  const viewDocument = (document, index) => {
    setNumPages(null);
    setPageNumber(1);
    setPdfError(null);
    setScale(1.0);
    setPdfLoading(true);

    setSelectedDocument({
      ...document,
      index: index + 1,
      url: document.file_url || document.file,
    });
    setShowDocumentModal(true);
    setIsFullscreen(false);
  };

  // PDF document load success
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
    setPdfError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error);
    setPdfLoading(false);
    setPdfError(
      'Failed to load PDF. The file might be corrupted or inaccessible.',
    );
  };

  const goToPrevPage = () => {
    if (pageNumber > 1) setPageNumber(pageNumber - 1);
  };

  const goToNextPage = () => {
    if (pageNumber < numPages) setPageNumber(pageNumber + 1);
  };

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3.0));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));

  const getFileInfo = (fileUrl) => {
    if (!fileUrl) return { type: 'unknown', icon: <FaFileAlt /> };
    const extension = fileUrl.split('.').pop().toLowerCase();

    if (extension === 'pdf') {
      return { type: 'pdf', icon: <FaFilePdf /> };
    } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
      return { type: 'image', icon: <FaFileImage /> };
    } else if (['doc', 'docx'].includes(extension)) {
      return { type: 'word', icon: <FaFileWord /> };
    }
    return { type: 'other', icon: <FaFileAlt /> };
  };

  const printDocument = () => {
    const printWindow = window.open();
    if (selectedDocument && printWindow) {
      const fileInfo = getFileInfo(selectedDocument.url);
      if (fileInfo.type === 'image') {
        printWindow.document.write(`
          <html><head><title>Document Viewer</title>
          <style>body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;}img{max-width:100%;height:auto;}</style>
          </head><body><img src="${selectedDocument.url}" /></body></html>
        `);
        printWindow.document.close();
        printWindow.print();
      } else {
        printWindow.location.href = selectedDocument.url;
        setTimeout(() => printWindow.print(), 1000);
      }
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'status-approved';
      case 'rejected':
        return 'status-rejected';
      default:
        return 'status-pending';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <FaUserCheck />;
      case 'rejected':
        return <FaTimes />;
      default:
        return <FaUserClock />;
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredApplications.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const stats = {
    total: applications.length,
    pending: applications.filter((app) => app.application_status === 'pending')
      .length,
    approved: applications.filter(
      (app) => app.application_status === 'approved',
    ).length,
    rejected: applications.filter(
      (app) => app.application_status === 'rejected',
    ).length,
  };

  return (
    <>
      <FurnitureNavbar />
      <div className="admin-applications-page">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <h1>
              <FaUsers /> Artisan Applications
            </h1>
            <p>Review and manage artisan applications</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-container">
          <div className="stat-card total">
            <div className="stat-icon">
              <FaUsers />
            </div>
            <div className="stat-info">
              <h3>{stats.total}</h3>
              <p>Total Applications</p>
            </div>
          </div>
          <div className="stat-card pending">
            <div className="stat-icon">
              <FaClock />
            </div>
            <div className="stat-info">
              <h3>{stats.pending}</h3>
              <p>Pending Review</p>
            </div>
          </div>
          <div className="stat-card approved">
            <div className="stat-icon">
              <FaCheck />
            </div>
            <div className="stat-info">
              <h3>{stats.approved}</h3>
              <p>Approved</p>
            </div>
          </div>
          <div className="stat-card rejected">
            <div className="stat-icon">
              <FaTimes />
            </div>
            <div className="stat-info">
              <h3>{stats.rejected}</h3>
              <p>Rejected</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-container">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-tabs">
            <button
              className={`filter-tab ${statusFilter === 'all' ? 'active' : ''}`}
              onClick={() => setStatusFilter('all')}
            >
              All ({stats.total})
            </button>
            <button
              className={`filter-tab ${statusFilter === 'pending' ? 'active' : ''}`}
              onClick={() => setStatusFilter('pending')}
            >
              Pending ({stats.pending})
            </button>
            <button
              className={`filter-tab ${statusFilter === 'approved' ? 'active' : ''}`}
              onClick={() => setStatusFilter('approved')}
            >
              Approved ({stats.approved})
            </button>
            <button
              className={`filter-tab ${statusFilter === 'rejected' ? 'active' : ''}`}
              onClick={() => setStatusFilter('rejected')}
            >
              Rejected ({stats.rejected})
            </button>
          </div>
        </div>

        {/* Applications Table */}
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading applications...</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="applications-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Experience</th>
                    <th>Documents</th>
                    <th>Applied Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((application) => (
                      <tr key={application.id}>
                        <td>#{application.id}</td>
                        <td>
                          <div className="applicant-name">
                            <strong>{application.name}</strong>
                          </div>
                        </td>
                        <td>
                          <div className="contact-info">
                            <div>
                              <FaEnvelope /> {application.email}
                            </div>
                            <div>
                              <FaPhone /> {application.phone_number}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="experience">
                            <FaBriefcase /> {application.years_of_experience}{' '}
                            years
                          </div>
                        </td>
                        <td>
                          <div className="documents-count">
                            <FaFileAlt />
                            <span>
                              {application.documents?.length || 0} files
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="date">
                            <FaCalendarAlt />
                            {new Date(
                              application.created_at,
                            ).toLocaleDateString()}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`status-badge ${getStatusBadgeClass(application.application_status)}`}
                          >
                            {getStatusIcon(application.application_status)}
                            {application.application_status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="view-btn"
                              onClick={() =>
                                viewApplicationDetails(application)
                              }
                              title="View Details"
                            >
                              <FaEye />
                            </button>
                            {application.application_status === 'pending' && (
                              <>
                                <button
                                  className="approve-btn"
                                  onClick={() =>
                                    handleApproveWithTier(application)
                                  }
                                  title="Approve"
                                >
                                  <FaCheck />
                                </button>
                                <button
                                  className="reject-btn"
                                  onClick={() =>
                                    handleRejectApplication(application.id)
                                  }
                                  title="Reject"
                                >
                                  <FaTimes />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="no-data">
                        <div className="empty-state">
                          <FaUsers />
                          <p>No applications found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <FaChevronLeft />
                </button>
                <div className="page-numbers">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum =
                      totalPages <= 5
                        ? i + 1
                        : currentPage <= 3
                          ? i + 1
                          : currentPage >= totalPages - 2
                            ? totalPages - 4 + i
                            : currentPage - 2 + i;
                    return (
                      <button
                        key={pageNum}
                        className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => paginate(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  className="pagination-btn"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <FaChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Application Details Modal */}
      <Modal
        show={showDetailsModal}
        onHide={() => setShowDetailsModal(false)}
        size="lg"
        centered
        className="application-details-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Application Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedApplication && (
            <div className="application-details">
              <div className="details-section">
                <h3>
                  <FaUserGraduate /> Personal Information
                </h3>
                <div className="details-grid">
                  <div className="detail-item">
                    <label>Full Name:</label>
                    <span>{selectedApplication.name}</span>
                  </div>
                  <div className="detail-item">
                    <label>Email:</label>
                    <span>{selectedApplication.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>Phone Number:</label>
                    <span>{selectedApplication.phone_number}</span>
                  </div>
                  <div className="detail-item">
                    <label>Years of Experience:</label>
                    <span>{selectedApplication.years_of_experience} years</span>
                  </div>
                  {selectedApplication.portfolio_url && (
                    <div className="detail-item">
                      <label>Portfolio:</label>
                      <a
                        href={selectedApplication.portfolio_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View Portfolio
                      </a>
                    </div>
                  )}
                  <div className="detail-item full-width">
                    <label>Bio:</label>
                    <p>{selectedApplication.bio || 'No bio provided'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Applied Date:</label>
                    <span>
                      {new Date(
                        selectedApplication.created_at,
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span
                      className={`status-badge ${getStatusBadgeClass(selectedApplication.application_status)}`}
                    >
                      {getStatusIcon(selectedApplication.application_status)}
                      {selectedApplication.application_status}
                    </span>
                  </div>
                </div>
              </div>
              <div className="details-section">
                <h3>
                  <FaFileAlt /> Submitted Documents
                </h3>
                {selectedApplication.documents &&
                selectedApplication.documents.length > 0 ? (
                  <div className="documents-list">
                    {selectedApplication.documents.map((doc, index) => (
                      <div key={doc.id || index} className="document-item">
                        <div className="document-info">
                          {getFileInfo(doc.file_url || doc.file).icon}
                          <div className="document-details">
                            <span className="document-name">
                              Document {index + 1}
                            </span>
                            <small className="document-date">
                              Uploaded:{' '}
                              {new Date(doc.uploaded_at).toLocaleDateString()}
                            </small>
                          </div>
                        </div>
                        <button
                          className="view-document-btn"
                          onClick={() => viewDocument(doc, index)}
                        >
                          <FaEye /> View Document
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-documents">No documents uploaded</p>
                )}
              </div>
              <div className="details-actions">
                {selectedApplication.application_status === 'pending' && (
                  <>
                    <button
                      className="approve-action-btn"
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleApproveWithTier(selectedApplication);
                      }}
                    >
                      <FaCheck /> Approve Application
                    </button>
                    <button
                      className="reject-action-btn"
                      onClick={() => {
                        handleRejectApplication(selectedApplication.id);
                        setShowDetailsModal(false);
                      }}
                    >
                      <FaTimes /> Reject Application
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDetailsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Approve Modal with Tier Selection */}
      {showApproveModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>✅ Approve Artisan Application</h2>
              <p>
                Review the artisan's information and select their tier level.
                This will create their account and send an activation email.
              </p>
              <button
                className="close-modal"
                onClick={() => setShowApproveModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSubmitApproval}>
              {/* Application Preview */}
              <div className="application-preview">
                <h4>📋 Application Details</h4>
                <div className="preview-grid">
                  <div className="preview-item">
                    <label>Full Name:</label>
                    <span>{selectedApplication?.name}</span>
                  </div>
                  <div className="preview-item">
                    <label>Email:</label>
                    <span>{selectedApplication?.email}</span>
                  </div>
                  <div className="preview-item">
                    <label>Phone:</label>
                    <span>{selectedApplication?.phone_number}</span>
                  </div>
                  <div className="preview-item">
                    <label>Experience:</label>
                    <span>
                      {selectedApplication?.years_of_experience} years
                    </span>
                  </div>
                  <div className="preview-item full-width">
                    <label>Bio:</label>
                    <p>{selectedApplication?.bio || 'No bio provided'}</p>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="full_name">
                  Full Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  className={formErrors.full_name ? 'error' : ''}
                  disabled={isSubmitting}
                />
                {formErrors.full_name && (
                  <span className="error-message">{formErrors.full_name}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className={formErrors.email ? 'error' : ''}
                  disabled={isSubmitting}
                />
                {formErrors.email && (
                  <span className="error-message">{formErrors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="phone_number">
                  Phone Number <span className="required">*</span>
                </label>
                <PhoneInput
                  country="ke"
                  value={formData.phone_number}
                  onChange={(phone) =>
                    setFormData({ ...formData, phone_number: phone })
                  }
                  inputStyle={{ width: '100%', height: '45px' }}
                  buttonStyle={{ border: 'none' }}
                  containerStyle={{ width: '100%' }}
                  countryCodeEditable={false}
                  disableDropdown={true}
                  enableLongNumbers={true}
                />
                {formErrors.phone_number && (
                  <span className="error-message">
                    {formErrors.phone_number}
                  </span>
                )}
                <div className="phone-hint">
                  <small>
                    Enter a valid Kenyan phone number (e.g., 0700000000 or
                    +254700000000)
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="tier">
                  Artisan Tier <span className="required">*</span>
                </label>
                <select
                  id="tier"
                  name="tier"
                  value={formData.tier}
                  onChange={(e) =>
                    setFormData({ ...formData, tier: e.target.value })
                  }
                  className={`tier-select-input ${formErrors.tier ? 'error' : ''}`}
                  disabled={isSubmitting}
                >
                  <option value="bronze">🥉 Bronze - Basic</option>
                  <option value="silver">🥈 Silver - Enhanced</option>
                  <option value="gold">🥇 Gold - Premium</option>
                </select>
                {formErrors.tier && (
                  <span className="error-message">{formErrors.tier}</span>
                )}

                <div className="tier-benefits-preview">
                  <h4>✨ Selected Tier Benefits:</h4>
                  <ul>
                    {getTierBenefits(formData.tier).map((benefit, index) => (
                      <li key={index}>{benefit}</li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="form-note">
                <p>
                  💡 <strong>What happens next:</strong>
                </p>
                <ul>
                  <li>
                    An artisan account will be created with the selected tier
                  </li>
                  <li>
                    The artisan will receive an activation email with
                    instructions
                  </li>
                  <li>
                    They will be able to set their own password upon activation
                  </li>
                  <li>Application status will be updated to "Approved"</li>
                </ul>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowApproveModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner"></span> Processing...
                    </>
                  ) : (
                    <>Approve & Create Artisan</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      <Modal
        show={showDocumentModal}
        onHide={() => {
          setShowDocumentModal(false);
          setSelectedDocument(null);
          setIsFullscreen(false);
          setNumPages(null);
          setPageNumber(1);
          setPdfError(null);
        }}
        size={isFullscreen ? 'xl' : 'lg'}
        centered={!isFullscreen}
        className={`document-viewer-modal ${isFullscreen ? 'fullscreen' : ''}`}
        fullscreen={isFullscreen}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div className="document-modal-title">
              {selectedDocument && getFileInfo(selectedDocument.url).icon}
              <span>Document {selectedDocument?.index || 1}</span>
            </div>
          </Modal.Title>
          <div className="document-modal-actions">
            {getFileInfo(selectedDocument?.url).type === 'pdf' && (
              <>
                <button
                  className="action-icon-btn"
                  onClick={zoomOut}
                  title="Zoom Out"
                  disabled={scale <= 0.5}
                >
                  <FaSearchMinus />
                </button>
                <span className="zoom-level">{Math.round(scale * 100)}%</span>
                <button
                  className="action-icon-btn"
                  onClick={zoomIn}
                  title="Zoom In"
                  disabled={scale >= 3.0}
                >
                  <FaSearchPlus />
                </button>
              </>
            )}
            <button
              className="action-icon-btn"
              onClick={() => setIsFullscreen(!isFullscreen)}
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <FaCompress /> : <FaExpand />}
            </button>
            <button
              className="action-icon-btn"
              onClick={printDocument}
              title="Print"
            >
              <FaPrint />
            </button>
          </div>
        </Modal.Header>
        <Modal.Body>
          {selectedDocument && (
            <div className="document-viewer-content">
              {getFileInfo(selectedDocument.url).type === 'image' && (
                <div className="image-viewer">
                  <img
                    src={selectedDocument.url}
                    alt={`Document ${selectedDocument.index}`}
                    onClick={() => setIsFullscreen(!isFullscreen)}
                  />
                </div>
              )}
              {getFileInfo(selectedDocument.url).type === 'pdf' && (
                <div className="pdf-viewer">
                  {pdfLoading && (
                    <div className="pdf-loading">
                      <FaSpinner className="spinner" />
                      <p>Loading PDF...</p>
                    </div>
                  )}
                  {pdfError && (
                    <div className="pdf-error">
                      <FaFilePdf />
                      <p>{pdfError}</p>
                      <button
                        className="open-in-new-tab"
                        onClick={() =>
                          window.open(selectedDocument.url, '_blank')
                        }
                      >
                        Open PDF in New Tab
                      </button>
                    </div>
                  )}
                  <Document
                    file={selectedDocument.url}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                      <div className="pdf-loading">
                        <FaSpinner className="spinner" />
                        <p>Loading PDF...</p>
                      </div>
                    }
                  >
                    <Page pageNumber={pageNumber} scale={scale} />
                  </Document>
                  {numPages && !pdfError && (
                    <div className="pdf-controls">
                      <button
                        onClick={goToPrevPage}
                        disabled={pageNumber <= 1}
                        className="page-nav-btn"
                      >
                        Previous
                      </button>
                      <span>
                        Page {pageNumber} of {numPages}
                      </span>
                      <button
                        onClick={goToNextPage}
                        disabled={pageNumber >= numPages}
                        className="page-nav-btn"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
              {getFileInfo(selectedDocument.url).type === 'word' && (
                <div className="word-viewer">
                  <div className="word-viewer-message">
                    <FaFileWord />
                    <p>Microsoft Word Document</p>
                    <p className="file-name">
                      {selectedDocument.url?.split('/').pop()}
                    </p>
                    <div className="word-viewer-actions">
                      <a
                        href={selectedDocument.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="open-in-new-tab"
                      >
                        Open in New Tab
                      </a>
                      <button
                        className="view-raw-btn"
                        onClick={() =>
                          window.open(selectedDocument.url, '_blank')
                        }
                      >
                        Download to View
                      </button>
                    </div>
                    <small>
                      Word documents are best viewed after downloading or in a
                      new tab.
                    </small>
                  </div>
                </div>
              )}
              {getFileInfo(selectedDocument.url).type === 'other' && (
                <div className="other-viewer">
                  <div className="other-viewer-message">
                    <FaFileAlt />
                    <p>File cannot be previewed directly</p>
                    <p className="file-name">
                      {selectedDocument.url?.split('/').pop()}
                    </p>
                    <div className="other-viewer-actions">
                      <a
                        href={selectedDocument.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="open-in-new-tab"
                      >
                        Open in New Tab
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="document-navigation">
            {selectedDocument && selectedDocument.index > 1 && (
              <Button
                variant="primary"
                onClick={() => {
                  const prevDoc =
                    selectedApplication?.documents[selectedDocument.index - 2];
                  if (prevDoc) {
                    setNumPages(null);
                    setPageNumber(1);
                    setPdfError(null);
                    setScale(1.0);
                    setPdfLoading(true);
                    setSelectedDocument({
                      ...prevDoc,
                      index: selectedDocument.index - 1,
                      url: prevDoc.file_url || prevDoc.file,
                    });
                  }
                }}
              >
                Previous Document
              </Button>
            )}
            <Button
              variant="secondary"
              onClick={() => setShowDocumentModal(false)}
            >
              Close
            </Button>
            {selectedDocument &&
              selectedDocument.index <
                (selectedApplication?.documents?.length || 0) && (
                <Button
                  variant="primary"
                  onClick={() => {
                    const nextDoc =
                      selectedApplication?.documents[selectedDocument.index];
                    if (nextDoc) {
                      setNumPages(null);
                      setPageNumber(1);
                      setPdfError(null);
                      setScale(1.0);
                      setPdfLoading(true);
                      setSelectedDocument({
                        ...nextDoc,
                        index: selectedDocument.index + 1,
                        url: nextDoc.file_url || nextDoc.file,
                      });
                    }
                  }}
                >
                  Next Document
                </Button>
              )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default withAuth(AdminArtisanApplications, 'admin');
